export interface ModelPricing {
  input: number;
  /** 5-minute-TTL cache write rate (1.25x input). Kept as `cacheWrite` for back-compat with existing saved overrides. */
  cacheWrite: number;
  /** 1-hour-TTL cache write rate (2x input). Optional so older saved overrides (pre-TTL-split) still parse. */
  cacheWrite1h?: number;
  cacheRead: number;
  output: number;
}

/**
 * Default Anthropic pricing per million tokens, keyed by model ID. Verified
 * live against platform.claude.com/docs/en/about-claude/pricing on 2026-07-30.
 * NOTE: the authoritative source of truth for RECORDED usage cost is now the
 * backend (each usage row carries its own immutable pricing snapshot) - this
 * table is a display-only fallback for data that predates that per-call
 * pricing snapshot, and for the admin override editor below.
 */
export const DEFAULT_MODEL_PRICES: Record<string, ModelPricing> = {
  'claude-sonnet-4-6':         { input: 3.00, cacheWrite: 3.75, cacheWrite1h: 6.00,  cacheRead: 0.30, output: 15.00 },
  'claude-haiku-4-5-20251001': { input: 1.00, cacheWrite: 1.25, cacheWrite1h: 2.00,  cacheRead: 0.10, output: 5.00  },
  'claude-opus-4-8':           { input: 5.00, cacheWrite: 6.25, cacheWrite1h: 10.00, cacheRead: 0.50, output: 25.00 },
  'claude-sonnet-4-5':         { input: 3.00, cacheWrite: 3.75, cacheWrite1h: 6.00,  cacheRead: 0.30, output: 15.00 },
  'claude-haiku-4-5':          { input: 1.00, cacheWrite: 1.25, cacheWrite1h: 2.00,  cacheRead: 0.10, output: 5.00  },
};

const FALLBACK_PRICE: ModelPricing = DEFAULT_MODEL_PRICES['claude-sonnet-4-6'];

let _overridePrices: Record<string, ModelPricing> = {};

/** Call once on app load with the admin-overridden prices fetched from settings. */
export function setModelPriceOverrides(overrides: Record<string, ModelPricing>): void {
  _overridePrices = overrides;
}

export function getPricing(model?: string | null): ModelPricing {
  if (model) {
    if (_overridePrices[model]) return _overridePrices[model];
    // Partial prefix match — e.g. "claude-sonnet-4-6-20251015" → "claude-sonnet-4-6"
    const key = Object.keys(DEFAULT_MODEL_PRICES).find(k => model.startsWith(k));
    if (key) return DEFAULT_MODEL_PRICES[key];
  }
  return FALLBACK_PRICE;
}

export interface RawTokens {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

/**
 * Effective input tokens — collapses all input-side token types into a
 * cost-weighted count. Ratios (cache_write=1.25x, cache_read=0.10x) are
 * constant across all Anthropic Claude models.
 */
export function effectiveInput(t: RawTokens): number {
  return Math.round(
    t.inputTokens
    + t.cacheWriteTokens * 1.25
    + t.cacheReadTokens  * 0.10,
  );
}

/** Estimated cost in USD. Pass model to use the correct per-model pricing. */
export function estimatedCostUsd(t: RawTokens, model?: string | null): number {
  const p = getPricing(model);
  return (
    t.inputTokens      * p.input      +
    t.cacheWriteTokens * p.cacheWrite +
    t.cacheReadTokens  * p.cacheRead  +
    t.outputTokens     * p.output
  ) / 1_000_000;
}

export function formatCostUsd(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.0001) return '<$0.0001';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

export function formatCostUsdFromNano(nanoStr: string | null | undefined): string {
  if (nanoStr === null || nanoStr === undefined) return '—';
  let nano: bigint;
  try {
    nano = BigInt(nanoStr);
  } catch {
    return '—';
  }
  if (nano === 0n) return '$0.00';
  const negative = nano < 0n;
  const abs = negative ? -nano : nano;
  const sign = negative ? '-' : '';
  if (abs < 100_000n) return `${sign}<$0.0001`; // < 0.0001 USD = 100_000 nano
  if (abs < 10_000_000n) {
    // < $0.01 - show 4 decimal places (1e-4 USD = 100_000 nano)
    const frac4 = abs / 100_000n;
    return `${sign}$0.${frac4.toString().padStart(4, '0')}`;
  }
  // >= $0.01 - show 2 decimal places, rounded half-up to the nearest cent (1 cent = 10_000_000 nano)
  const cents = (abs + 5_000_000n) / 10_000_000n;
  const dollars = cents / 100n;
  const centsRemainder = cents % 100n;
  return `${sign}$${dollars}.${centsRemainder.toString().padStart(2, '0')}`;
}
